import React, { useState, useEffect } from 'react';
import { Card, Button, Chip, CardBody } from '@material-tailwind/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROAD_ADMINISTRATORS } from '../data/constants.js';
import { CustomAddressInput } from '../components/formInputs/CustomAddressInput';
import { 
  QuestionMarkCircleIcon, 
  BugAntIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  XCircleIcon,
  ChatBubbleLeftRightIcon, 
  PencilSquareIcon,
  ArrowLeftIcon, 
  CalendarDaysIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const HelpDesk = () => {
  const tel_guichet = process.env.REACT_APP_TEL_GUICHET;
  const opening_guichet = process.env.REACT_APP_OPENING_GUICHET;

  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [userPath, setUserPath] = useState({});
  const { t } = useTranslation();

  const [openPopover, setOpenPopover] = useState(false);

  const [addressLatitude, setAddressLatitude] = useState(0);
  const [addressLongitude, setAddressLongitude] = useState(0);

  const handleInputChangeAddress = (value) => {
    if (value.latitude && value.longitude) {
      setAddressLatitude(value.latitude);
      setAddressLongitude(value.longitude);
    }
  };

  const handleValidityChangeAddress = (fieldName, isValid) => {
  // Validity change logic
  };

  const AsyncStepCheckAdministratorComponent = () => {
    const [content, setContent] = useState(null);

    useEffect(() => {
      const loadContent = async () => {
        const administrator = await checkAdministratorBasedOnAddress();
        setContent(
          administrator.value === ROAD_ADMINISTRATORS.REGION
            ? renderContactAssistanceRegion()
            : renderContactAssistanceMunicipality(administrator)
        );
      };
      loadContent();
    }, []);

    return content ||  
    <div className="flex font-bold text-blue-900 text-xl">
      {t('contactSupportLoadingData')} 
    </div>;
  };

  const roadAdministrators = [
    { value: ROAD_ADMINISTRATORS.REGION, label: t('roadAdministratorRegion'), infoLink: "/procedures" },
    { value: ROAD_ADMINISTRATORS.AUDERGHEM, label: t('roadAdministratorAuderghem'), infoLink: "/procedures#AUD" },
    { value: ROAD_ADMINISTRATORS.ANDERLECHT, label: t('roadAdministratorAnderlecht'), infoLink: "/procedures#AND" },
    { value: ROAD_ADMINISTRATORS.JETTE, label: t('roadAdministratorJette'), infoLink: "/procedures#JET" },
    { value: ROAD_ADMINISTRATORS.BERCHEM_SAINTE_AGATHE, label: t('roadAdministratorBerchemSainteAgathe'), infoLink: "/procedures#BER" },
    { value: ROAD_ADMINISTRATORS.EVERE, label: t('roadAdministratorEvere'), infoLink: "/procedures#EVE" },
    { value: ROAD_ADMINISTRATORS.ETTERBEEK, label: t('roadAdministratorEtterbeek'), infoLink: "/procedures#ETT" },
    { value: ROAD_ADMINISTRATORS.KOEKELBERG, label: t('roadAdministratorKoekelberg'), infoLink: "/procedures#KOEK" },
    { value: ROAD_ADMINISTRATORS.SCHAERBEEK, label: t('roadAdministratorSchaerbeek'), infoLink: "/procedures#SCH" },
    { value: ROAD_ADMINISTRATORS.VILLE_DE_BRUXELLES, label: t('roadAdministratorVilleDeBruxelles'), infoLink: "/procedures#BRU" },
    { value: ROAD_ADMINISTRATORS.FOREST, label: t('roadAdministratorForest'), infoLink: "/procedures#FOR" },
    { value: ROAD_ADMINISTRATORS.MOLENBEEK_SAINT_JEAN, label: t('roadAdministratorMolenbeekSaintJean'), infoLink: "/procedures#MOL" },
    { value: ROAD_ADMINISTRATORS.UCCLE, label: t('roadAdministratorUccle'), infoLink: "/procedures#UCL" },
    { value: ROAD_ADMINISTRATORS.GANSHOREN, label: t('roadAdministratorGanshoren'), infoLink: "/procedures#GAN" },
    { value: ROAD_ADMINISTRATORS.SAINT_GILLES, label: t('roadAdministratorSaintGilles'), infoLink: "/procedures#STG" },
    { value: ROAD_ADMINISTRATORS.WATERMAEL_BOITSFORT, label: t('roadAdministratorWatermaelBoitsfort'), infoLink: "/procedures#WAT" },
    { value: ROAD_ADMINISTRATORS.IXELLES, label: t('roadAdministratorIxelles'), infoLink: "/procedures#ELS" },
    { value: ROAD_ADMINISTRATORS.SAINT_JOSSE_TEN_NOODE, label: t('roadAdministratorSaintJosseTenNoode'), infoLink: "/procedures#STJ" },
    { value: ROAD_ADMINISTRATORS.WOLUWE_SAINT_LAMBERT, label: t('roadAdministratorWoluweSaintLambert'), infoLink: "/procedures#WSL" },
    { value: ROAD_ADMINISTRATORS.WOLUWE_SAINT_PIERRE, label: t('roadAdministratorWoluweSaintPierre'), infoLink: "/procedures#WSP" }
];
  
  const handleNext = (choice) => {
    setUserPath({ ...userPath, [activeStep]: choice });
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      const newUserPath = { ...userPath };
      delete newUserPath[activeStep - 1];
      setUserPath(newUserPath);
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const BackButton = () => {
    if (activeStep === 0) return null;
    return (
      <Button
        variant="text"
        size="sm"
        className="self-start flex items-center gap-2 text-blue-900 dark:text-yellow-800"
        onClick={handleBack}
      >
        <ArrowLeftIcon className="h-5 w-5" />
        {t('previous-step')}
      </Button>
    );
  };

  const LinkButton = ({ title, link, internal, icon, isPhoneButton }) => {
    const isMobileDevice = () => {
      return (
        typeof window.orientation !== "undefined" ||
        navigator.userAgent.indexOf("IEMobile") !== -1
      );
    };

    const handleClick = (event) => {
      event.stopPropagation();
      if (isPhoneButton) {
        if (isMobileDevice()) {
          window.location.href = `tel:${link}`;
        } else {
          setOpenPopover(true);
        }
      } else if (internal) {
        navigate(link);
      } else {
        window.open(link, '_blank', 'noopener noreferrer');
      }
    };

    return (
      <>
        <Button
          variant="outlined"
          size="sm"
          color="blue-gray"
          className="bg-white flex flex-row gap-2 text-justify rounded text-sm normal-case dark:text-white dark:hover:bg-blue-1000 dark:bg-blue-900"
          onClick={(e) => handleClick(e)}
        >
          <div className="h-5 w-5 strokewidth=2">
            {icon}
          </div>
          {title}
        </Button>

        <div className="fixed bottom-20 right-4 z-40">
          {openPopover &&
            <div className="fixed bottom-20 right-4 z-40">
              <Card>
                <CardBody className="relative">
                  <div className="flex flex-row gap-4 items-center">
                    <PhoneIcon className="h-5 w-5" />
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row gap-2">{t('sd-phonepart')} <strong>{tel_guichet}</strong></div>
                      <div>{t('sd-phonepart2')} : {opening_guichet}</div>
                    </div>
                  </div>
                  <button onClick={() => setOpenPopover(false)} className="absolute top-0 right-0 p-2">
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </CardBody>
              </Card>
            </div>
          }
        </div>
      </>
    );
  };

  const HandleButton = ({ icon, title, toHandle }) => (
    <Button
      variant="outlined"
      size="sm"
      color="blue-gray"
      className="bg-white flex flex-row gap-2 text-justify rounded text-sm normal-case dark:text-white dark:hover:bg-blue-1000 dark:bg-blue-900"
      onClick={() => handleNext(toHandle)}
    >
      {icon}
      {title}
    </Button>
  );

  // Méthode pour afficher le contenu des ressources: FAQ, Vidéos, Manuel, Contact  guichet
  const renderHelpResourcesContent = () => {
    return (
      <div className="flex flex-col items-center gap-8 p-4 max-w-screen-sm w-full mx-auto">
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-row gap-4 w-full">
            <Chip 
              className="self-start whitespace-normal bg-blue-50 text-blue-900 normal-case text-xl p-4 rounded-lg w-7/8 max-w-[87.5%]" 
              variant="filled" 
              value={t('findAnswersHere')} 
            />
          </div>
          <BackButton />
        </div>

        <div className="flex flex-col gap-4 p-4 rounded-lg bg-blue-gray-50 w-7/8 max-w-[87.5%] ml-auto">
          <LinkButton title={t('linkFAQ')} link="/faq" internal={true} />
          <LinkButton title={t('linkTutorialVideo')} link="/tutorials#vids" internal={true} />
          <LinkButton title={t('linkUserManual')} link="/manual#manual" internal={true} />
          <HandleButton title={t('moreHelpNeeded')} toHandle="needAssistanceFromSupport" />
        </div>
      </div>
    );
  };

  // Méthode pour afficher les modes de contact avec le guichet
  const renderContactAssistanceBasedOnAddress = () => {
    return (
      <div className="flex flex-col items-center gap-8 p-4 max-w-screen-sm w-full mx-auto">
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-row gap-4 w-full">
            <Chip 
              className="self-start whitespace-normal bg-blue-50 text-blue-900 normal-case text-xl p-4 rounded-lg w-7/8 max-w-[87.5%]" 
              variant="filled" 
              value={t('contactSupportAddressText')} 
            />
          </div>
          <BackButton />
        </div>

        <div className=" w-full">
          <CustomAddressInput
            label={t('cf-address')}
            fieldname="address"
            onChange={(value) => handleInputChangeAddress(value)}
            setValidity={handleValidityChangeAddress}/>
        </div>

          <HandleButton title={t('contactSupportAddressValidate')} toHandle="needAssistanceFromSupportBasedOnAddress" />
      </div>
    );
  };

  const checkAdministratorBasedOnAddress = async () => {
    try {
      // Construct the API URL with the latitude and longitude values
      const apiUrl = `https://data.mobility.brussels/query/onregionalroad/?x=${addressLongitude}&y=${addressLatitude}&srs=4326`;
      
      console.log("Calling API with URL:", apiUrl);
      
      // Make the API call
      const response = await axios.get(apiUrl);
      console.log("API Response:", response.data);
      
      if (response.data && response.data.road_management === "REG") {
        // If road management is REG, set to REGION
        return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.REGION);
      }
      else if (response.data && response.data.municipality && response.data.municipality.fr) {
        const municipality = response.data.municipality.fr.toUpperCase();

        console.log("municipality UPPERCASE:", municipality);
        
        if (municipality === "AUDERGHEM") 
          return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.AUDERGHEM);
        else if (municipality === "ANDERLECHT") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.ANDERLECHT);
        else if (municipality === "BERCHEM-SAINTE-AGATHE") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.BERCHEM_SAINTE_AGATHE);
        else if (municipality === "BRUXELLES")
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.VILLE_DE_BRUXELLES);
        else if (municipality === "ETTERBEEK")
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.ETTERBEEK);
        else if (municipality === "EVERE") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.EVERE);
        else if (municipality === "FOREST") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.FOREST);
        else if (municipality === "GANSHOREN") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.GANSHOREN);
        else if (municipality === "IXELLES") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.IXELLES);
        else if (municipality === "JETTE") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.JETTE);
        else if (municipality === "KOEKELBERG") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.KOEKELBERG);
        else if (municipality === "MOLENBEEK-SAINT-JEAN") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.MOLENBEEK_SAINT_JEAN);
        else if (municipality === "SAINT-GILLES") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.SAINT_GILLES);
        else if (municipality === "SAINT-JOSSE-TEN-NOODE")
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.SAINT_JOSSE_TEN_NOODE);
        else if (municipality === "SCHAERBEEK") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.SCHAERBEEK);
        else if (municipality === "UCCLE") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.UCCLE);
        else if (municipality === "WATERMAEL-BOITSFORT") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.WATERMAEL_BOITSFORT);
        else if (municipality === "WOLUWE-SAINT-LAMBERT") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.WOLUWE_SAINT_LAMBERT);
        else if (municipality === "WOLUWE-SAINT-PIERRE") 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.WOLUWE_SAINT_PIERRE);
        else 
            return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.REGION);
        }
      else {
        return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.REGION);
      }
    } catch (error) {
      console.error("Error fetching administrator data:", error);
      // Fallback to REGION in case of error
      return roadAdministrators.find(admin => admin.value === ROAD_ADMINISTRATORS.REGION);
    }
  };


  // Méthode pour afficher les modes de contact avec le guichet pour la région
  const renderContactAssistanceRegion = () => {
    return (
      <div className="flex flex-col items-center gap-8 p-4 max-w-screen-sm w-full mx-auto">
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-row gap-4 w-full">
            <Chip 
              className="self-start whitespace-normal bg-blue-50 text-blue-900 normal-case text-xl p-4 rounded-lg w-7/8 max-w-[87.5%]" 
              variant="filled" 
              value={t('howContactHelpdesk')} 
            />
          </div>
          <BackButton />
        </div>

        <div className="flex flex-col gap-4 p-4 rounded-lg bg-blue-gray-50 w-7/8 max-w-[87.5%] ml-auto">
          <LinkButton icon={<EnvelopeIcon className="h-5 w-5" />} title={t('contactViaForm')} link="/help-assistance" internal={true} />
          <LinkButton icon={<PhoneIcon className="h-5 w-5" />} title={t('contactViaCall')} isPhoneButton link={tel_guichet} />
          <LinkButton icon={<CalendarDaysIcon className="h-5 w-5" />} title={t('contactViaSchedulingMeeting')} link="/simply-meet" internal={true} />
        </div>
      </div>
    );
  };

  // Méthode pour afficher les modes de contact avec le guichet pour les communes
  const renderContactAssistanceMunicipality = (roadAdministratorAssociatedToAddress) => {
    return (
      <div className="flex flex-col items-center gap-8 p-4 max-w-screen-sm w-full mx-auto">
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-row gap-4 w-full">
            <Chip 
              className="self-start whitespace-normal bg-blue-50 text-blue-900 normal-case text-xl p-4 rounded-lg w-7/8 max-w-[87.5%]" 
              variant="filled" 
              value={t('contactSupportMunicipality',{ administratorLabel: roadAdministratorAssociatedToAddress.label})} 
            />
          </div>
          <BackButton />
        </div>

        <div className="flex flex-col gap-4 p-4 rounded-lg bg-blue-gray-50 w-7/8 max-w-[87.5%] ml-auto">
          <LinkButton icon={<BuildingOffice2Icon className="h-5 w-5" />} title={t('contactSupportMunicipalityGoToProcedures',{ administratorLabel: roadAdministratorAssociatedToAddress.label})} link={roadAdministratorAssociatedToAddress.infoLink} internal={true} />
        </div>
      </div>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="flex flex-col items-center gap-8 p-4 max-w-screen-sm w-full mx-auto">
            <div className="flex flex-col w-full gap-2">
              <div className="flex flex-row gap-4 w-full">
                <Chip 
                  className="self-start whitespace-normal bg-blue-50 text-blue-900 normal-case text-xl p-4  w-7/8 max-w-[87.5%]" 
                  variant="filled" 
                  value={t('helpToday')} 
                />
              </div>
              <BackButton />
            </div>

            <div className="flex flex-col gap-4 p-4 rounded-lg bg-blue-gray-50">
              <LinkButton icon={<PencilSquareIcon className="h-5 w-5" />} title={t('newRequestOsiris')} link="/faq" internal={true} />
              <HandleButton icon={<QuestionMarkCircleIcon className="h-5 w-5" />} title={t('UserQuestionOnOsiris')} toHandle="questionAboutOsiris" />
              <HandleButton icon={<ChatBubbleLeftRightIcon className="h-5 w-5" />} title={t('needAssistance')} toHandle="needAssistance" />
              <LinkButton icon={<BugAntIcon className="h-5 w-5" />} title={t('UserReportABug')} link="/bug-report" internal={true} />
            </div>
          </div>
        );
      
      case 1:
        if (userPath[0] === 'questionAboutOsiris' || userPath[0] === 'needAssistance') {
          return renderHelpResourcesContent();
        }
        break;
      
      case 2:
        if (userPath[1] === 'needAssistanceFromSupport') {
          return renderContactAssistanceRegion();
          //POUR ACTIVER LA DICHOTOMIE GUICHET REGION / REDIRECTION COMMUNE: REMPLACER renderContactAssistanceRegion() par renderContactAssistanceBasedOnAddress() 
          //return renderContactAssistanceBasedOnAddress();
        }
        break;
    
        case 3:
          if (userPath[2] === 'needAssistanceFromSupportBasedOnAddress') {
            return <AsyncStepCheckAdministratorComponent />;
          }
          break;
      
      default:
        return null;
    }
  };
  
  // Reset function to go back to the initial state
  const resetHelpDesk = () => {
    setActiveStep(0);
    setUserPath({});
  };
  
  // Special handling for specific path choices that might need reset
  useEffect(() => {
    if (userPath[activeStep] === 'restartHelpDesk') {
      resetHelpDesk();
    }
  }, [userPath, activeStep]);
  
  return (
    <div className="flex flex-col md:p-4 items-center p-4">
      <Card className="dark:bg-blue-700">
        <CardBody>
          <div className="text-3xl font-bold text-blue-900 dark:text-yellow-800">
            <a href="/helpdesk" className="hover:underline">
              Helpdesk
            </a>
          </div>
          
          <div className="py-8 dark:bg-blue-700">
            {getStepContent(activeStep)}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
  
export default HelpDesk;